import Dexie, { Entity, type EntityTable } from 'dexie'
import { toRaw } from 'vue'
import { PermanentId } from '@/shared/types/multiplayer.ts'
import { Deck, DeckList } from '@/shared/types/gateway.ts'

// If you know, you know ;-)
export const DEFAULT_PLAYER_NAME = 'The Unnamed'

export enum DeckSource {
    Precon = 'Precon',
    Vdb = 'VDB',
    VtesDecks = 'VtesDecks',
    Amaranth = 'Amaranth',
    Text = 'Text',
}

export enum WorldAlignment {
    TopRight = 'TopRight',
    Center = 'Center',
}

export type UserPreferences = {
    worldAlignment?: WorldAlignment
    glowInHand?: number // We cannot index boolean with Dexie, so fallback on 0=false / 1=true
    glowInPlay?: number // We cannot index boolean with Dexie, so fallback on 0=false / 1=true
    snapToGrid?: number // We cannot index boolean with Dexie, so fallback on 0=false / 1=true
    alignmentGuides?: number // We cannot index boolean with Dexie, so fallback on 0=false / 1=true
    cardGrouping?: number // We cannot index boolean with Dexie, so fallback on 0=false / 1=true
    actionDeclaration?: number // We cannot index boolean with Dexie, so fallback on 0=false / 1=true
    usageEditor?: number // We cannot index boolean with Dexie, so fallback on 0=false / 1=true
    turnNotification?: number // We cannot index boolean with Dexie, so fallback on 0=false / 1=true
    showBleedTarget?: number // We cannot index boolean with Dexie, so fallback on 0=false / 1=true
    automaticCostPayment?: number // We cannot index boolean with Dexie, so fallback on 0=false / 1=true
    // CommandName ==> character
    keyBindings?: Record<string, { repr: string; keyCode: number }>
}

export class DbUserProfile extends Entity<SuccubusDb> {
    id: number
    permanentId: PermanentId
    playerName: string
    avatar: string | null
    preferences: UserPreferences
    lastDeckId: number | null

    static async get() {
        let userProfile = await db.userProfile.limit(1).first()
        if (!userProfile) {
            db.userProfile.add({
                permanentId: crypto.randomUUID(),
                playerName: DEFAULT_PLAYER_NAME,
                avatar: null,
                preferences: {},
                lastDeckId: null,
            })
            userProfile = await db.userProfile.limit(1).first()

            // If still undefined after adding, something went wrong
            if (!userProfile) {
                throw new Error('Failed to create or retrieve user profile')
            }
        }

        return userProfile
    }

    async save() {
        // toRaw is needed to save as a plain object, without reactivity wrappers
        await db.userProfile.put(toRaw(this))
    }
}

export class DbDeck extends Entity<SuccubusDb> implements Deck {
    id: number
    name: string
    source: DeckSource
    sourceValue: string
    cards: DeckList
    lastUsed: Date

    static async create(name: string, cards: DeckList, source: DeckSource, sourceValue: string) {
        const deckId = await db.decks.add({
            name,
            source,
            sourceValue,
            cards,
            lastUsed: new Date(),
        })
        const deck = await DbDeck.get(deckId)
        if (!deck) {
            throw new Error('Error while creating the deck')
        }
        return deck as DbDeck
    }

    static async get(deckId: number) {
        return (await db.decks.get(deckId)) ?? null
    }

    async delete() {
        await db.decks.delete(this.id)
    }
}

/**
 * A generic key-value store, handy for binary blobs (e.g. the custom tabletop
 * background) that don't belong in the frequently-saved user profile.
 */
export class DbSetting extends Entity<SuccubusDb> {
    key: string
    value: unknown
}

export class SuccubusDb extends Dexie {
    userProfile: EntityTable<DbUserProfile, 'id'>
    decks: EntityTable<DbDeck, 'id'>
    // The exclamation mark is here to fix a known Dexie + TypeScript issue
    // caused by Dexie's complex generic type chain.
    settings!: EntityTable<DbSetting, 'key'>

    constructor() {
        super('SuccubusDb')
        this.version(1).stores({
            userProfile: '++id',
            decks: '++id, lastUsed',
            savedGames: '++id, isAutoSave, date',
        })
        this.version(2).stores({
            settings: 'key',
        })
        this.version(3).stores({
            savedGames: null,
        })
        this.userProfile.mapToClass(DbUserProfile)
        this.decks.mapToClass(DbDeck)
        this.settings.mapToClass(DbSetting)
    }
}

export const db = new SuccubusDb()
